/**
 * Connector Service
 * 
 * Provides integration interfaces for external systems.
 * Makes J2 (Integrations support session inputs/outputs) true.
 */

import type { StoredFieldRules } from '../../supabase/functions/_shared/fieldSyncRules';
import { getSupabaseClient } from './supabaseClient';

// ============================================================================
// Types
// ============================================================================

export type ConnectorType = 
  | 'jira'
  | 'asana'
  | 'monday'
  | 'adobe-worksheet'
  | 'celoxis'
  | 'wrike'
  | 'productive'
  | 'notion'
  | 'linear'
  | 'trello'
  | 'github'
  | 'gitlab'
  | 'airtable'
  | 'sheets'
  | 'csv'
  | 'webhook'
  | 'api'
  | 'miro'
  | 'whiteboard'
  | 'slack'
  | 'clickup'
  | 'google-calendar'
  | 'zoom'
  | 'google-meet'
  | 'microsoft-teams'
  // Customer & Market Signal
  | 'intercom'
  | 'zendesk'
  | 'hubspot'
  | 'salesforce'
  // Knowledge & Docs (content data sources for AI access)
  | 'google-docs'
  | 'dropbox'
  // Research & User Insights
  | 'dovetail'
  | 'maze'
  | 'fullstory'
  // Data & Engineering
  | 'datadog'
  | 'snowflake'
  // AI & Automation
  | 'perplexity'
  | 'loom'
  | 'fireflies'
  | 'granola'
  | 'n8n'
  | 'make'
  | 'raycast'
  | 'mcp'
  // OKR
  | 'quantive'
  // Design
  | 'figma';

export type ConnectorDirection = 'import' | 'export' | 'bidirectional';

export type ConnectorStatus = 'active' | 'inactive' | 'error' | 'pending_auth';

export interface ConnectorConfig {
  id: string;
  name: string;
  type: ConnectorType;
  direction: ConnectorDirection;
  status: ConnectorStatus;
  workspaceId: string;
  
  // Authentication
  authType: 'oauth' | 'api_key' | 'basic' | 'none';
  credentials?: {
    accessToken?: string;
    refreshToken?: string;
    apiKey?: string;
    expiresAt?: number;
  };
  
  // Configuration
  config: Record<string, unknown>;
  
  // Mapping
  fieldMapping?: FieldMapping[];
  
  // Metadata
  createdAt: string;
  updatedAt: string;
  lastSyncAt?: string;
  lastError?: string;
}

export interface FieldMapping {
  sourceField: string;
  targetField: string;
  transform?: 'none' | 'lowercase' | 'uppercase' | 'date' | 'number' | 'boolean' | 'custom';
  customTransform?: string;
  required: boolean;
}

export interface ImportResult {
  success: boolean;
  totalRecords: number;
  importedCount: number;
  skippedCount: number;
  errorCount: number;
  errors?: { record: unknown; error: string }[];
  importedItems: ImportedItem[];
}

export interface ImportedItem {
  externalId: string;
  internalId: string;
  title: string;
  source: ConnectorType;
  importedAt: string;
}

export interface ExportResult {
  success: boolean;
  totalRecords: number;
  exportedCount: number;
  errorCount: number;
  errors?: { itemId: string; error: string }[];
  externalUrl?: string;
}

// ============================================================================
// Connector Registry
// ============================================================================

interface ConnectorDefinition {
  type: ConnectorType;
  name: string;
  description: string;
  iconUrl?: string;
  authType: ConnectorConfig['authType'];
  supportedDirections: ConnectorDirection[];
  configSchema: Record<string, { type: string; required: boolean; description: string }>;
}

export const CONNECTOR_REGISTRY: ConnectorDefinition[] = [
  {
    type: 'miro',
    name: 'Miro',
    description: 'Import sticky notes from Miro boards as ideas for prioritization',
    authType: 'oauth',
    supportedDirections: ['import', 'export'],
    configSchema: {
      boardId: { type: 'string', required: true, description: 'Miro board ID to import from' },
      teamId: { type: 'string', required: false, description: 'Miro team ID (optional)' },
      colorMapping: { type: 'object', required: false, description: 'Map sticky note colors to categories' },
      templateId: { type: 'string', required: false, description: 'Template for zone-based metadata' },
    },
  },
  {
    type: 'slack',
    name: 'Slack',
    description: 'Sync ideas and comments with Slack channels, publish events',
    authType: 'oauth',
    supportedDirections: ['bidirectional'],
    configSchema: {
      teamId: { type: 'string', required: false, description: 'Slack team ID (set by OAuth install)' },
      teamName: { type: 'string', required: false, description: 'Slack team name (set by OAuth install)' },
      channelId: { type: 'string', required: false, description: 'Default channel ID' },
      scope: { type: 'string', required: false, description: 'Channel scope (organization or team)' },
    },
  },
  {
    type: 'jira',
    name: 'Jira',
    description: 'Import issues from Jira, export priorities as epics',
    authType: 'oauth',
    supportedDirections: ['import', 'export', 'bidirectional'],
    configSchema: {
      projectKey: { type: 'string', required: true, description: 'Jira project key' },
      issueTypes: { type: 'array', required: false, description: 'Issue types to sync' },
      jqlFilter: { type: 'string', required: false, description: 'JQL filter for imports' },
    },
  },
  {
    type: 'asana',
    name: 'Asana',
    description: 'Sync tasks and projects with Asana',
    authType: 'oauth',
    supportedDirections: ['import', 'export', 'bidirectional'],
    configSchema: {
      workspaceId: { type: 'string', required: true, description: 'Asana workspace ID' },
      projectId: { type: 'string', required: false, description: 'Specific project to sync' },
    },
  },
  {
    type: 'notion',
    name: 'Notion',
    description: 'Import from Notion databases, export priority lists',
    authType: 'oauth',
    supportedDirections: ['import', 'export'],
    configSchema: {
      databaseId: { type: 'string', required: true, description: 'Notion database ID' },
    },
  },
  {
    type: 'linear',
    name: 'Linear',
    description: 'Sync issues with Linear',
    authType: 'oauth',
    supportedDirections: ['import', 'export', 'bidirectional'],
    configSchema: {
      teamId: { type: 'string', required: true, description: 'Linear team ID' },
    },
  },
  {
    type: 'github',
    name: 'GitHub',
    description: 'Import issues from GitHub repositories, export priorities as issues',
    authType: 'oauth',
    supportedDirections: ['import', 'export'],
    configSchema: {
      owner: { type: 'string', required: true, description: 'Repository owner' },
      repo: { type: 'string', required: true, description: 'Repository name' },
      labels: { type: 'array', required: false, description: 'Filter by labels' },
    },
  },
  {
    type: 'clickup',
    name: 'ClickUp',
    description: 'Import tasks from ClickUp, export priorities as tasks',
    authType: 'oauth',
    supportedDirections: ['import', 'export'],
    configSchema: {
      workspaceId: { type: 'string', required: true, description: 'ClickUp workspace ID' },
      listId: { type: 'string', required: false, description: 'ClickUp list ID' },
    },
  },
  {
    type: 'monday',
    name: 'monday.com',
    description:
      'Sync boards, timelines, and work items between monday.com and your backlog — mirror priorities wherever your teams plan delivery.',
    authType: 'oauth',
    supportedDirections: ['import', 'export', 'bidirectional'],
    configSchema: {
      workspaceId: { type: 'string', required: true, description: 'monday.com workspace ID' },
      boardId: { type: 'string', required: false, description: 'Default board ID to sync from' },
    },
  },
  {
    type: 'adobe-worksheet',
    name: 'Adobe Worksheet',
    description:
      'Bring worksheet-backed plans into item context — keep structured creative and stakeholder updates alongside prioritization cycles.',
    authType: 'oauth',
    supportedDirections: ['import', 'export'],
    configSchema: {
      workbookId: { type: 'string', required: false, description: 'Linked workbook identifier' },
    },
  },
  {
    type: 'celoxis',
    name: 'Celoxis',
    description:
      'Mirror projects and portfolios from Celoxis so PMO timelines, allocations, and risks inform what gets prioritized.',
    authType: 'oauth',
    supportedDirections: ['import', 'export', 'bidirectional'],
    configSchema: {
      siteUrl: { type: 'string', required: false, description: 'Celoxis site URL' },
    },
  },
  {
    type: 'wrike',
    name: 'Wrike',
    description:
      'Pull tasks, approvals, and campaign folders from Wrike into item evidence — align cross-functional queues with prioritized outcomes.',
    authType: 'oauth',
    supportedDirections: ['import', 'export', 'bidirectional'],
    configSchema: {
      workspaceId: { type: 'string', required: false, description: 'Wrike workspace/folder scope' },
    },
  },
  {
    type: 'productive',
    name: 'Productive',
    description:
      'Connect budgets, utilization, and margin signals from Productive.io — prioritize with agency profitability and capacity in view.',
    authType: 'oauth',
    supportedDirections: ['import', 'bidirectional'],
    configSchema: {
      organizationId: { type: 'string', required: false, description: 'Productive organization ID' },
    },
  },
  {
    type: 'google-calendar',
    name: 'Google Calendar',
    description: 'Schedule prioritization activities with automatic calendar invites',
    authType: 'oauth',
    supportedDirections: ['export'],
    configSchema: {
      calendarId: { type: 'string', required: false, description: 'Calendar ID (default: primary)' },
      defaultDuration: { type: 'number', required: false, description: 'Default event duration in minutes' },
      sendUpdates: { type: 'boolean', required: false, description: 'Send invite emails to attendees' },
    },
  },
  {
    type: 'zoom',
    name: 'Zoom',
    description: 'Create Zoom meetings for prioritization sessions, and ingest cloud-recording transcripts and in-meeting chat so what was said and requested feeds the backlog',
    authType: 'oauth',
    supportedDirections: ['import', 'export', 'bidirectional'],
    configSchema: {
      defaultDuration: { type: 'number', required: false, description: 'Default meeting duration in minutes' },
      autoRecord: { type: 'boolean', required: false, description: 'Automatically record meetings (required to capture a transcript)' },
      waitingRoom: { type: 'boolean', required: false, description: 'Enable waiting room' },
      ingestTranscripts: { type: 'boolean', required: false, description: 'Import cloud-recording transcripts as items/evidence' },
      ingestChat: { type: 'boolean', required: false, description: 'Import in-meeting chat as items/evidence' },
    },
  },
  {
    type: 'microsoft-teams',
    name: 'Microsoft Teams',
    description: 'Ingest Teams online-meeting transcripts and meeting chat via Microsoft Graph — customer requests, commitments, and rationale from meetings become candidate backlog items',
    authType: 'oauth',
    supportedDirections: ['import'],
    configSchema: {
      tenantId: { type: 'string', required: false, description: 'Microsoft Entra ID tenant (set by OAuth install; defaults to the signed-in tenant)' },
      ingestTranscripts: { type: 'boolean', required: false, description: 'Import meeting transcripts as items/evidence' },
      ingestChat: { type: 'boolean', required: false, description: 'Import meeting chat as items/evidence' },
    },
  },
  {
    type: 'google-meet',
    name: 'Google Meet',
    description: 'Generate Google Meet links for prioritization activities, and ingest meeting transcripts (via the Google Meet REST API v2) so what was said and requested feeds the backlog',
    authType: 'oauth',
    supportedDirections: ['import', 'export', 'bidirectional'],
    configSchema: {
      calendarId: { type: 'string', required: false, description: 'Calendar ID to attach Meet links to' },
      defaultDuration: { type: 'number', required: false, description: 'Default meeting duration in minutes' },
      ingestTranscripts: { type: 'boolean', required: false, description: 'Import meeting transcripts as items/evidence (chat is not exposed by Google’s API)' },
    },
  },
  {
    type: 'quantive',
    name: 'Quantive',
    description: 'Link priorities.ai items to Quantive OKRs — Objectives and Key Results — for bidirectional strategy alignment',
    authType: 'api_key',
    supportedDirections: ['bidirectional'],
    configSchema: {
      apiKey: { type: 'string', required: true, description: 'Quantive API key' },
      accountId: { type: 'string', required: true, description: 'Quantive account ID' },
    },
  },
  {
    type: 'granola',
    name: 'Granola',
    description: 'Link priorities.ai items to Granola meeting notes — trace decisions back to the conversations where they were made',
    authType: 'api_key',
    supportedDirections: ['import'],
    configSchema: {
      apiKey: { type: 'string', required: true, description: 'Granola API key (starts with grn_)' },
    },
  },
  {
    type: 'figma',
    name: 'Figma',
    description: 'Import Figma frames as items, export priorities back as comments, and link individual items to specific Figma frames',
    authType: 'api_key',
    supportedDirections: ['import', 'export', 'bidirectional'],
    configSchema: {
      accessToken: { type: 'string', required: true, description: 'Figma personal access token' },
      teamId: { type: 'string', required: false, description: 'Figma team ID (optional — needed to browse projects)' },
    },
  },
  {
    type: 'csv',
    name: 'CSV Import/Export',
    description:
      'Spreadsheet sync — import and export items as a CSV file, now or on a schedule (set up under Settings → Spreadsheet sync)',
    authType: 'none',
    supportedDirections: ['import', 'export'],
    configSchema: {
      delimiter: { type: 'string', required: false, description: 'CSV delimiter (default: comma)' },
      hasHeaders: { type: 'boolean', required: false, description: 'First row is headers' },
    },
  },
  {
    type: 'webhook',
    name: 'Webhook',
    description: 'Send priority updates to a webhook URL',
    authType: 'api_key',
    supportedDirections: ['export'],
    configSchema: {
      webhookUrl: { type: 'string', required: true, description: 'Webhook URL' },
      events: { type: 'array', required: false, description: 'Events to trigger webhook' },
    },
  },
  {
    type: 'api',
    name: 'Custom API',
    description: 'Connect to a custom REST API',
    authType: 'api_key',
    supportedDirections: ['import', 'export', 'bidirectional'],
    configSchema: {
      baseUrl: { type: 'string', required: true, description: 'API base URL' },
      importEndpoint: { type: 'string', required: false, description: 'Endpoint for imports' },
      exportEndpoint: { type: 'string', required: false, description: 'Endpoint for exports' },
    },
  },
  // ── Customer & Market Signal ─────────────────────────────────────────────
  {
    type: 'intercom',
    name: 'Intercom',
    description: 'Read Intercom conversations — surface themes, CSAT signals, and feature requests as candidate backlog items and demand straight from your users',
    authType: 'oauth',
    supportedDirections: ['import'],
    // Intercom REST API pinned to Intercom-Version 2.14. Auth is the OAuth 2.0
    // authorization-code flow (redirect + Eagle token exchange) — no pasted
    // token. Region selects the data-hosting base URL.
    configSchema: {
      region: { type: 'string', required: false, description: 'Data region: us (default), eu, or au' },
    },
  },
  {
    type: 'zendesk',
    name: 'Zendesk',
    description: 'Read Zendesk Support tickets — pull ticket volume, tags, and CSAT trends as demand signals that reveal what your support queue means for your backlog',
    authType: 'oauth',
    supportedDirections: ['import'],
    // Zendesk Support API v2 with the OAuth 2.0 authorization-code + refresh
    // flow (short-lived access tokens for clients created on/after 2026-04-30).
    // The legacy email/api-token Basic-auth path is intentionally not used —
    // Zendesk is retiring API tokens (all deactivate 2027-04-30). Only the
    // subdomain is entered; tokens are obtained via the OAuth redirect.
    configSchema: {
      subdomain: { type: 'string', required: true, description: 'Your Zendesk subdomain (the {subdomain} in {subdomain}.zendesk.com)' },
    },
  },
  {
    type: 'hubspot',
    name: 'HubSpot',
    description: 'Map deal stages and pipeline weight to backlog items — understand what sales is promising and align priorities accordingly',
    authType: 'oauth',
    supportedDirections: ['import'],
    // HubSpot public app via OAuth 2.0 authorization-code + refresh flow (access
    // tokens expire ~30 min). Scopes: crm.objects.deals.read + crm.schemas.deals.read.
    // The portal ID is DISCOVERED from the token metadata during the OAuth
    // callback (GET /oauth/v1/access-tokens/{token} → hub_id) — it is not a
    // manual input, so it is not a required config field. See
    // engines/integrations-engine/src/services/hubspotService.ts.
    configSchema: {
      portalId: { type: 'string', required: false, description: 'HubSpot portal ID (discovered via OAuth; not entered manually)' },
    },
  },
  {
    type: 'salesforce',
    name: 'Salesforce',
    description: 'Surface opportunity revenue and customer requests as weighted evidence — see which items are blocking deals or renewals',
    authType: 'oauth',
    supportedDirections: ['import'],
    configSchema: {
      instanceUrl: { type: 'string', required: true, description: 'Salesforce instance URL' },
    },
  },
  // ── Knowledge & Docs (content data sources for AI access) ────────────────
  {
    type: 'google-docs',
    name: 'Google Docs',
    description: 'Connect Google Docs as a content data source — let AI read your specs, briefs, and decision docs, and keep rationale living alongside the work',
    authType: 'oauth',
    supportedDirections: ['import'],
    configSchema: {
      folderId: { type: 'string', required: false, description: 'Optional Google Drive folder ID to scope which documents are available' },
    },
  },
  {
    type: 'dropbox',
    name: 'Dropbox',
    description: 'Connect Dropbox as a content data source — let AI read the files, specs, and notes your team already stores in Dropbox',
    authType: 'oauth',
    supportedDirections: ['import'],
    configSchema: {
      path: { type: 'string', required: false, description: 'Optional Dropbox folder path to scope which files are available (default: entire Dropbox)' },
    },
  },
  {
    type: 'sheets',
    name: 'Google Sheets',
    description: 'Import structured item lists from Sheets or push a live priority snapshot — a lightweight alternative to CSV for teams already in Google Workspace',
    authType: 'oauth',
    supportedDirections: ['import', 'export'],
    configSchema: {
      spreadsheetId: { type: 'string', required: false, description: 'Google Sheets spreadsheet ID' },
    },
  },
  // ── Research & User Insights ─────────────────────────────────────────────
  {
    type: 'dovetail',
    name: 'Dovetail',
    description: 'Pull tagged qualitative research themes directly into item evidence — close the gap between user research and what gets prioritized',
    authType: 'api_key',
    supportedDirections: ['import'],
    configSchema: {
      apiKey: { type: 'string', required: true, description: 'Dovetail API key' },
      projectId: { type: 'string', required: false, description: 'Dovetail project ID to sync from' },
    },
  },
  {
    type: 'maze',
    name: 'Maze',
    description: 'Feed usability test results and task success rates into item readiness scores — let real user data drive prioritization decisions',
    authType: 'api_key',
    supportedDirections: ['import'],
    configSchema: {
      apiKey: { type: 'string', required: true, description: 'Maze API key' },
    },
  },
  {
    type: 'fullstory',
    name: 'FullStory',
    description: 'Surface rage clicks, friction signals, and session replay data as evidence on relevant backlog items — behavioral signals at the point of prioritization',
    authType: 'api_key',
    supportedDirections: ['import'],
    configSchema: {
      apiKey: { type: 'string', required: true, description: 'FullStory API key' },
      orgId: { type: 'string', required: true, description: 'FullStory org ID' },
    },
  },
  // ── Data & Engineering ───────────────────────────────────────────────────
  {
    type: 'datadog',
    name: 'Datadog',
    description: 'Pull SLO status, incident counts, and error rates as evidence on reliability items — let observability data inform what engineering prioritizes next',
    authType: 'api_key',
    supportedDirections: ['import'],
    configSchema: {
      apiKey: { type: 'string', required: true, description: 'Datadog API key' },
      appKey: { type: 'string', required: true, description: 'Datadog application key' },
      site: { type: 'string', required: false, description: 'Datadog site (e.g. datadoghq.com)' },
    },
  },
  {
    type: 'snowflake',
    name: 'Snowflake',
    description: 'Query your data warehouse directly to enrich items with usage metrics, revenue attribution, and custom business signals at prioritization time',
    // Snowflake SQL API v2 with a Programmatic Access Token (PAT, GA 2025) —
    // sent as a Bearer token, not username/password. See snowflakeService.ts.
    authType: 'api_key',
    supportedDirections: ['import'],
    configSchema: {
      account: { type: 'string', required: true, description: 'Snowflake account identifier (host prefix, e.g. myorg-myaccount)' },
      username: { type: 'string', required: true, description: 'Username the Programmatic Access Token was issued for' },
      token: { type: 'string', required: true, description: 'Programmatic Access Token (PAT) secret' },
      warehouse: { type: 'string', required: false, description: 'Snowflake warehouse name' },
      database: { type: 'string', required: false, description: 'Default database' },
      schema: { type: 'string', required: false, description: 'Default schema' },
      role: { type: 'string', required: false, description: 'Role to assume for queries' },
    },
  },
  // ── AI & Automation ──────────────────────────────────────────────────────
  {
    type: 'perplexity',
    name: 'Perplexity',
    description: 'Enrich backlog items with real-time research — competitor moves, market signals, and cited evidence — at the point of prioritization',
    authType: 'api_key',
    supportedDirections: ['import'],
    configSchema: {
      apiKey: { type: 'string', required: true, description: 'Perplexity API key' },
    },
  },
  {
    type: 'loom',
    name: 'Loom',
    description: 'Attach async video context to priority items — record stakeholder walkthroughs, rationale, or demos and link them directly',
    authType: 'oauth',
    supportedDirections: ['import', 'export'],
    configSchema: {
      workspaceId: { type: 'string', required: false, description: 'Loom workspace ID' },
    },
  },
  {
    type: 'fireflies',
    name: 'Fireflies.ai',
    description: 'Auto-import action items and decisions captured from recorded meetings directly into your backlog',
    authType: 'api_key',
    supportedDirections: ['import'],
    configSchema: {
      apiKey: { type: 'string', required: true, description: 'Fireflies API key' },
      autoImport: { type: 'boolean', required: false, description: 'Automatically import action items after each meeting' },
    },
  },
  {
    type: 'n8n',
    name: 'n8n',
    description: 'Build custom automation workflows — trigger priority updates from any source, or push results to any downstream system',
    authType: 'api_key',
    supportedDirections: ['import', 'export', 'bidirectional'],
    configSchema: {
      webhookUrl: { type: 'string', required: true, description: 'n8n webhook URL' },
      apiKey: { type: 'string', required: false, description: 'n8n API key for authenticated workflows' },
    },
  },
  {
    type: 'make',
    name: 'Make',
    description: 'Connect prioritization to 1,000+ apps via Make (formerly Integromat) — no-code automation for power users',
    authType: 'api_key',
    supportedDirections: ['import', 'export', 'bidirectional'],
    configSchema: {
      webhookUrl: { type: 'string', required: true, description: 'Make webhook URL' },
    },
  },
  {
    type: 'raycast',
    name: 'Raycast',
    description: 'Add items, search priorities, and act on your backlog from the Raycast launcher — keyboard-first, zero context-switching',
    authType: 'api_key',
    supportedDirections: ['import', 'export'],
    configSchema: {
      apiKey: { type: 'string', required: true, description: 'API key for the Raycast extension' },
    },
  },
  {
    type: 'mcp',
    name: 'MCP Endpoint',
    description: 'Expose priorities as a Model Context Protocol resource — any AI agent or IDE (Cursor, Claude, etc.) can query and update your backlog natively',
    authType: 'api_key',
    supportedDirections: ['import', 'export', 'bidirectional'],
    configSchema: {
      apiKey: { type: 'string', required: true, description: 'MCP endpoint API key' },
      allowedClients: { type: 'array', required: false, description: 'Allowed MCP client IDs' },
    },
  },
];

// ============================================================================
// Connector Service Functions
// ============================================================================

/**
 * Get available connectors
 */
export function getAvailableConnectors(): ConnectorDefinition[] {
  return CONNECTOR_REGISTRY;
}

/**
 * Get connector definition by type
 */
export function getConnectorDefinition(type: ConnectorType): ConnectorDefinition | undefined {
  return CONNECTOR_REGISTRY.find(c => c.type === type);
}

/**
 * Create a new connector configuration (not yet persisted)
 */
export function createConnectorConfig(
  type: ConnectorType,
  name: string,
  workspaceId: string,
  direction: ConnectorDirection,
  config: Record<string, unknown>
): Omit<ConnectorConfig, 'id' | 'createdAt' | 'updatedAt'> {
  const definition = getConnectorDefinition(type);
  
  if (!definition) {
    throw new Error(`Unknown connector type: ${type}`);
  }
  
  if (!definition.supportedDirections.includes(direction)) {
    throw new Error(`Connector ${type} does not support ${direction}`);
  }
  
  return {
    name,
    type,
    direction,
    status: 'pending_auth',
    workspaceId,
    authType: definition.authType,
    config,
  };
}

/**
 * Validate connector configuration
 */
export function validateConnectorConfig(
  type: ConnectorType,
  config: Record<string, unknown>
): { valid: boolean; errors: string[] } {
  const definition = getConnectorDefinition(type);
  
  if (!definition) {
    return { valid: false, errors: [`Unknown connector type: ${type}`] };
  }
  
  const errors: string[] = [];
  
  for (const [field, schema] of Object.entries(definition.configSchema)) {
    if (schema.required && (config[field] === undefined || config[field] === '')) {
      errors.push(`Required field missing: ${field}`);
    }
  }
  
  return { valid: errors.length === 0, errors };
}

// ============================================================================
// Import/Export Operations
// ============================================================================

/**
 * Import items from an external source
 */
export async function importFromConnector(
  connectorId: string,
  options?: {
    dryRun?: boolean;
    limit?: number;
    filter?: Record<string, unknown>;
  }
): Promise<ImportResult> {
  // This would be implemented per connector type
  // For now, return a placeholder result
  console.log(`Importing from connector ${connectorId}`, options);
  
  return {
    success: true,
    totalRecords: 0,
    importedCount: 0,
    skippedCount: 0,
    errorCount: 0,
    importedItems: [],
  };
}

/**
 * Export items to an external destination
 */
export async function exportToConnector(
  connectorId: string,
  itemIds: string[],
  options?: {
    includeScores?: boolean;
    includeMetadata?: boolean;
    format?: string;
  }
): Promise<ExportResult> {
  // This would be implemented per connector type
  console.log(`Exporting ${itemIds.length} items to connector ${connectorId}`, options);
  
  return {
    success: true,
    totalRecords: itemIds.length,
    exportedCount: itemIds.length,
    errorCount: 0,
  };
}

/**
 * Parse CSV content into importable items
 */
export function parseCSV(
  content: string,
  options: {
    delimiter?: string;
    hasHeaders?: boolean;
    fieldMapping?: FieldMapping[];
  } = {}
): { headers: string[]; rows: Record<string, string>[] } {
  const delimiter = options.delimiter || ',';
  const hasHeaders = options.hasHeaders !== false;
  
  const lines = content.trim().split('\n');
  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }
  
  const parseRow = (line: string): string[] => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (const char of line) {
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === delimiter && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    
    return values;
  };
  
  const headers = hasHeaders ? parseRow(lines[0]) : [];
  const dataLines = hasHeaders ? lines.slice(1) : lines;
  
  const rows = dataLines.map(line => {
    const values = parseRow(line);
    const row: Record<string, string> = {};
    
    if (hasHeaders) {
      headers.forEach((header, i) => {
        row[header] = values[i] || '';
      });
    } else {
      values.forEach((value, i) => {
        row[`column_${i}`] = value;
      });
    }
    
    return row;
  });
  
  return { headers, rows };
}

/**
 * Generate CSV content from items
 */
export function generateCSV(
  items: Array<Record<string, unknown>>,
  options: {
    fields?: string[];
    delimiter?: string;
    includeHeaders?: boolean;
  } = {}
): string {
  const delimiter = options.delimiter || ',';
  const includeHeaders = options.includeHeaders !== false;
  
  if (items.length === 0) {
    return '';
  }
  
  const fields = options.fields || Object.keys(items[0]);
  
  const escapeValue = (value: unknown): string => {
    const str = String(value ?? '');
    if (str.includes(delimiter) || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };
  
  const lines: string[] = [];
  
  if (includeHeaders) {
    lines.push(fields.map(escapeValue).join(delimiter));
  }
  
  for (const item of items) {
    const values = fields.map(field => escapeValue(item[field]));
    lines.push(values.join(delimiter));
  }
  
  return lines.join('\n');
}

// ============================================================================
// Webhook Support
// ============================================================================

export interface WebhookPayload {
  event: string;
  timestamp: string;
  data: Record<string, unknown>;
  signature?: string;
}

/**
 * Send webhook notification
 */
export async function sendWebhook(
  webhookUrl: string,
  event: string,
  data: Record<string, unknown>,
  apiKey?: string
): Promise<{ success: boolean; statusCode?: number; error?: string }> {
  const payload: WebhookPayload = {
    event,
    timestamp: new Date().toISOString(),
    data,
  };
  
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (apiKey) {
      headers['Authorization'] = `Bearer ${apiKey}`;
    }
    
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    
    return {
      success: response.ok,
      statusCode: response.status,
      error: response.ok ? undefined : `HTTP ${response.status}`,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// ============================================================================
// Sync Status Tracking
// ============================================================================

export interface SyncStatus {
  connectorId: string;
  direction: ConnectorDirection;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  startedAt: string;
  completedAt?: string;
  recordsProcessed: number;
  errors: string[];
}

const activeSyncs = new Map<string, SyncStatus>();

/**
 * Start a sync operation
 */
export function startSync(connectorId: string, direction: ConnectorDirection): SyncStatus {
  const status: SyncStatus = {
    connectorId,
    direction,
    status: 'in_progress',
    startedAt: new Date().toISOString(),
    recordsProcessed: 0,
    errors: [],
  };
  
  activeSyncs.set(connectorId, status);
  return status;
}

/**
 * Update sync progress
 */
export function updateSyncProgress(connectorId: string, recordsProcessed: number): void {
  const status = activeSyncs.get(connectorId);
  if (status) {
    status.recordsProcessed = recordsProcessed;
  }
}

/**
 * Complete a sync operation
 */
export function completeSync(connectorId: string, success: boolean, errors?: string[]): SyncStatus | undefined {
  const status = activeSyncs.get(connectorId);
  if (status) {
    status.status = success ? 'completed' : 'failed';
    status.completedAt = new Date().toISOString();
    if (errors) {
      status.errors = errors;
    }
  }
  return status;
}

/**
 * Get current sync status
 */
export function getSyncStatus(connectorId: string): SyncStatus | undefined {
  return activeSyncs.get(connectorId);
}

// ============================================================================
// DB-backed Connector Config Persistence (Phase 1)
//
// These functions read from and write to the connector_configs table.
// The in-memory activeSyncs map above remains as a cache for active sync
// operations within a single browser session. On page load, configs are
// fetched fresh from the DB via fetchConnectorConfigsFromDB().
// ============================================================================

export interface ConnectorConfigDB {
  id: string;
  workspaceId: string;
  /** Null for the workspace's shared connection; a person's id for their own. */
  userId: string | null;
  connectorType: ConnectorType;
  displayName: string | null;
  authData: Record<string, unknown>;
  /** Field Sync Rules (supabase/functions/_shared/fieldSyncRules.ts). */
  trustRules: StoredFieldRules;
  fieldMappings: FieldMapping[];
  syncDirection: 'inbound' | 'outbound' | 'both';
  isActive: boolean;
  lastSyncedAt: string | null;
  syncError: string | null;
  createdAt: string;
  updatedAt: string;
}

function rowToConnectorConfigDB(row: Record<string, unknown>): ConnectorConfigDB {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    userId: (row.user_id as string | null) ?? null,
    connectorType: row.connector_type as ConnectorType,
    displayName: (row.display_name as string) ?? null,
    authData: (row.auth_data as Record<string, unknown>) ?? {},
    trustRules: (row.trust_rules as StoredFieldRules) ?? {},
    fieldMappings: (row.field_mappings as FieldMapping[]) ?? [],
    syncDirection: (row.sync_direction as 'inbound' | 'outbound' | 'both') ?? 'both',
    isActive: (row.is_active as boolean) ?? true,
    lastSyncedAt: (row.last_synced_at as string) ?? null,
    syncError: (row.sync_error as string) ?? null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

/**
 * Fetch all connector configs for a workspace from the DB.
 */
export async function fetchConnectorConfigsFromDB(
  workspaceId: string,
): Promise<{ data: ConnectorConfigDB[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('connector_configs')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: true });

    if (error) return { data: [], error: error.message };
    return { data: (data ?? []).map(rowToConnectorConfigDB), error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Upsert a connector config in the DB.
 * If a config for (workspace_id, connector_type) already exists it is updated;
 * otherwise a new row is inserted.
 */
export async function saveConnectorConfigToDB(
  workspaceId: string,
  connectorType: ConnectorType,
  fields: {
    displayName?: string;
    authData?: Record<string, unknown>;
    trustRules?: StoredFieldRules;
    fieldMappings?: FieldMapping[];
    syncDirection?: 'inbound' | 'outbound' | 'both';
    isActive?: boolean;
  },
): Promise<{ data: ConnectorConfigDB | null; error: string | null }> {
  try {
    const supabase = getSupabaseClient();

    // Migration 406 uses a partial unique index (WHERE user_id IS NULL) which
    // PostgreSQL's ON CONFLICT (columns) cannot target. Do a manual update-or-insert.
    const { data: existing } = await supabase
      .from('connector_configs')
      .select('id')
      .eq('workspace_id', workspaceId)
      .eq('connector_type', connectorType)
      .is('user_id', null)
      .maybeSingle();

    // Build the full insert payload (with defaults for missing fields).
    const insertPayload = {
      workspace_id: workspaceId,
      connector_type: connectorType,
      display_name: fields.displayName ?? null,
      auth_data: fields.authData ?? {},
      trust_rules: fields.trustRules ?? {},
      field_mappings: fields.fieldMappings ?? [],
      sync_direction: fields.syncDirection ?? 'both',
      is_active: fields.isActive ?? true,
    };

    // For updates, only send fields that were explicitly provided so we don't
    // overwrite e.g. auth_data with {} when only field_mappings changed.
    const updatePayload: Record<string, unknown> = {};
    if (fields.displayName !== undefined)  updatePayload.display_name  = fields.displayName;
    if (fields.authData     !== undefined)  updatePayload.auth_data     = fields.authData;
    if (fields.trustRules   !== undefined)  updatePayload.trust_rules   = fields.trustRules;
    if (fields.fieldMappings !== undefined) updatePayload.field_mappings = fields.fieldMappings;
    if (fields.syncDirection !== undefined) updatePayload.sync_direction = fields.syncDirection;
    if (fields.isActive      !== undefined) updatePayload.is_active      = fields.isActive;

    let data: unknown, error: { message: string } | null;

    if (existing?.id) {
      ({ data, error } = await supabase
        .from('connector_configs')
        .update(updatePayload)
        .eq('id', existing.id)
        .select()
        .single());
    } else {
      ({ data, error } = await supabase
        .from('connector_configs')
        .insert(insertPayload)
        .select()
        .single());
    }

    if (error) return { data: null, error: (error as { message: string }).message };
    return { data: rowToConnectorConfigDB(data as Record<string, unknown>), error: null };
  } catch (err) {
    return { data: null, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Delete a connector config from the DB.
 */
export async function deleteConnectorConfigFromDB(
  workspaceId: string,
  connectorType: ConnectorType,
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('connector_configs')
      .delete()
      .eq('workspace_id', workspaceId)
      .eq('connector_type', connectorType);

    return { error: error ? error.message : null };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Update sync state on a connector config after a sync completes.
 */
export async function updateConnectorSyncState(
  workspaceId: string,
  connectorType: ConnectorType,
  state: { lastSyncedAt?: string; syncError?: string | null },
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const patch: Record<string, unknown> = {};
    if (state.lastSyncedAt !== undefined) patch.last_synced_at = state.lastSyncedAt;
    if (state.syncError !== undefined) patch.sync_error = state.syncError;
    const { error } = await supabase
      .from('connector_configs')
      .update(patch)
      .eq('workspace_id', workspaceId)
      .eq('connector_type', connectorType);

    return { error: error ? error.message : null };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

// ============================================================================
// Entity External ID Registry (Phase 3)
//
// registerExternalId() creates or updates a row in entity_external_ids
// linking a priorities.ai entity to its counterpart in an external system.
// Call this whenever a connector creates, imports, or links an item so
// the inbound applier (/apply edge function) can route future changes.
//
// getExternalIds() returns all known external IDs for a local entity,
// useful for building deep links and for the dispatch function.
//
// triggerApply() calls the /apply edge function from the browser to
// process pending inbound change_events.
// ============================================================================

export interface RegisterExternalIdParams {
  workspaceId: string;
  entityType: ChangeEventEntityType;
  internalId: string;
  connectorType: string;
  externalId: string;
  externalUrl?: string;
  /** The key a person recognizes (a Linear issue identifier such as PRI-12) when external_id is not it. */
  externalLabel?: string;
  externalProjectKey?: string;
  connectorConfigId?: string;
  /** The external record's version at this sync (its updated-at stamp), for change detection. */
  syncHash?: string;
  /** For a strategy object: what the record there says now, per field of ours (migration 1049). */
  externalValues?: Record<string, unknown>;
}

/**
 * Register (or update) an external ID mapping for a local entity.
 * Upserts on (workspace_id, connector_type, external_id) — a full unique index
 * since migration 950 (the partial one before it refused every upsert).
 * Returns the error message when the save was refused; a caller that only
 * fires and forgets may ignore it, but it is logged either way.
 */
export async function registerExternalId(
  params: RegisterExternalIdParams,
): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase.from('entity_external_ids').upsert(
      {
        workspace_id: params.workspaceId,
        entity_type: params.entityType,
        internal_id: params.internalId,
        connector_type: params.connectorType,
        external_id: params.externalId,
        // Absent = keep the link it has (a re-sync that knows no address must not clear it).
        ...(params.externalUrl ? { external_url: params.externalUrl } : {}),
        ...(params.externalLabel !== undefined ? { external_label: params.externalLabel } : {}),
        external_project_key: params.externalProjectKey ?? null,
        connector_config_id: params.connectorConfigId ?? null,
        ...(params.syncHash !== undefined ? { sync_hash: params.syncHash } : {}),
        ...(params.externalValues !== undefined ? { external_values: params.externalValues } : {}),
        last_synced_at: new Date().toISOString(),
        is_active: true,
      },
      { onConflict: 'workspace_id,connector_type,external_id' },
    );
    if (error) {
      console.warn('[registerExternalId] refused:', error.message);
      return { error: error.message };
    }
    return { error: null };
  } catch (err) {
    console.warn('[registerExternalId] failed:', err);
    return { error: err instanceof Error ? err.message : 'The link was not saved' };
  }
}

/**
 * Retire one external-id mapping (the link row stays for history, inactive).
 * Returns the error message when the update was refused.
 */
export async function deactivateExternalId(params: {
  workspaceId: string;
  entityType: ChangeEventEntityType;
  internalId: string;
  connectorType: string;
  externalId: string;
}): Promise<{ error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('entity_external_ids')
      .update({ is_active: false })
      .eq('workspace_id', params.workspaceId)
      .eq('entity_type', params.entityType)
      .eq('internal_id', params.internalId)
      .eq('connector_type', params.connectorType)
      .eq('external_id', params.externalId);
    return { error: error ? error.message : null };
  } catch (err) {
    return { error: err instanceof Error ? err.message : 'Unlink failed' };
  }
}

/**
 * Window event raised after an entity's external links change (linked,
 * synced, unlinked), so every open reader of them (useItemExternalLinks)
 * re-reads without a page reload.
 */
export const EXTERNAL_IDS_CHANGED_EVENT = 'external-ids-changed';

export function notifyExternalIdsChanged(entityType: string, internalId: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(EXTERNAL_IDS_CHANGED_EVENT, { detail: { entityType, internalId } }));
}

/**
 * Fetch all external ID mappings for a local entity.
 * Returns empty array on error.
 */
export async function getExternalIds(
  workspaceId: string,
  entityType: string,
  internalId: string,
): Promise<Array<{
  connectorType: string;
  externalId: string;
  /** The key a person recognizes, when it differs from externalId (migration 939). */
  externalLabel: string | null;
  externalUrl: string | null;
  lastSyncedAt: string | null;
  /** For a strategy object: what the record there last said, per field of ours (migration 1049). */
  externalValues: Record<string, unknown>;
}>> {
  try {
    const supabase = getSupabaseClient();
    const { data } = await supabase
      .from('entity_external_ids')
      .select('connector_type, external_id, external_label, external_url, last_synced_at, external_values')
      .eq('workspace_id', workspaceId)
      .eq('entity_type', entityType)
      .eq('internal_id', internalId)
      .eq('is_active', true);

    return (data ?? []).map((row) => ({
      connectorType: (row as Record<string, unknown>).connector_type as string,
      externalId: (row as Record<string, unknown>).external_id as string,
      externalLabel: ((row as Record<string, unknown>).external_label as string | null) ?? null,
      externalUrl: (row as Record<string, unknown>).external_url as string | null,
      lastSyncedAt: (row as Record<string, unknown>).last_synced_at as string | null,
      externalValues: ((row as Record<string, unknown>).external_values as Record<string, unknown> | null) ?? {},
    }));
  } catch {
    return [];
  }
}

/**
 * Call the /apply edge function to process pending inbound change_events.
 * Returns the summary from the function.
 */
export async function triggerApply(
  workspaceId: string,
  options?: { eventId?: string; mode?: 'pending' | 'negotiated' },
): Promise<{
  applied: number;
  conflicts: number;
  skipped: number;
  no_mapping: number;
  errors: number;
  error?: string;
}> {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? '';
    const url = new URL(`${supabaseUrl}/functions/v1/apply`);
    url.searchParams.set('workspace_id', workspaceId);
    if (options?.eventId) url.searchParams.set('event_id', options.eventId);
    if (options?.mode) url.searchParams.set('mode', options.mode);

    // /apply verifies its caller in-function: it needs the signed-in user's JWT
    // (a workspace admin), not an anonymous POST.
    const { data: sessionData } = await getSupabaseClient().auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) {
      return { applied: 0, conflicts: 0, skipped: 0, no_mapping: 0, errors: 0, error: 'Sign in required' };
    }
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...(anonKey ? { apikey: anonKey } : {}),
      },
    });
    const data = await response.json().catch(() => ({})) as Record<string, unknown>;

    if (!response.ok) {
      const message = typeof data.error === 'string' ? data.error : `HTTP ${response.status}`;
      return { applied: 0, conflicts: 0, skipped: 0, no_mapping: 0, errors: 0, error: message };
    }

    return {
      applied: (data.applied as number) ?? 0,
      conflicts: (data.conflicts as number) ?? 0,
      skipped: (data.skipped as number) ?? 0,
      no_mapping: (data.no_mapping as number) ?? 0,
      errors: (data.errors as number) ?? 0,
    };
  } catch (err) {
    return {
      applied: 0, conflicts: 0, skipped: 0, no_mapping: 0, errors: 0,
      error: err instanceof Error ? err.message : 'Apply request failed',
    };
  }
}

// ============================================================================
// Change Event Logging (Phase 1)
//
// Call logChangeEvent() whenever priorities.ai mutates a prioritization
// entity so the change is recorded in the append-only change_events log.
// External-origin events are inserted by the webhook edge function;
// this helper is for internal (priorities_ai source) events only.
// ============================================================================

export type ChangeEventEntityType =
  | 'item'
  | 'strategy'
  | 'objective'
  | 'goal'
  | 'cycle'
  | 'backplan_node'
  | 'roadmap_node'
  | 'outcome_driver'
  | 'desired_outcome'
  | 'open_question'
  | 'strategy_canvas';

export type ChangeEventType =
  | 'created'
  | 'updated'
  | 'deleted'
  | 'synced'
  | 'conflict_detected'
  | 'conflict_resolved'
  | 'rule_violated'
  | 'quarantined';

export interface LogChangeEventParams {
  workspaceId: string;
  entityType: ChangeEventEntityType;
  entityId: string;
  eventType: ChangeEventType;
  fieldName?: string;
  oldValue?: unknown;
  newValue?: unknown;
  /**
   * 'pending' (the default) waits for the dispatch function to send it out. A
   * record of something already done — a sync that came in — is 'synced', so it
   * is never mistaken for a change waiting to go out.
   */
  syncStatus?: 'pending' | 'synced';
}

/**
 * Insert a change event row originating from priorities.ai.
 * Fire-and-forget — caller does not need to await success.
 */
export async function logChangeEvent(
  params: LogChangeEventParams,
): Promise<void> {
  try {
    const supabase = getSupabaseClient();
    await supabase.from('change_events').insert({
      workspace_id: params.workspaceId,
      source: 'priorities_ai',
      entity_type: params.entityType,
      entity_id: params.entityId,
      field_name: params.fieldName ?? null,
      old_value: params.oldValue !== undefined ? params.oldValue : null,
      new_value: params.newValue !== undefined ? params.newValue : null,
      event_type: params.eventType,
      sync_status: params.syncStatus ?? 'pending',
    });
  } catch (err) {
    // Non-blocking — log to console but never throw
    console.warn('[logChangeEvent] failed to insert change event:', err);
  }
}

